import React from "react";
import { Compass, HeartHandshake } from "lucide-react";

const displayNumber = (code) => code?.notation || "—";
const titleCase = (key) => key.replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());
const LABELS = {
  esencia: "Esencia · día", aprendizaje: "Aprendizaje · mes", herencia: "Herencia · año",
  mision: "Misión · Camino de Vida", alma: "Alma · vocales",
  personalidad: "Personalidad · consonantes", expresion: "Expresión · nombre",
  year: "Año personal", month: "Mes personal", day: "Día personal",
  universal_year: "Año universal", universal_month: "Mes universal",
  actitud: "Actitud", generacion: "Generación", madurez: "Madurez",
  nombre_de_uso: "Nombre de uso", paternal_surname: "Linaje 1", maternal_surname: "Linaje 2",
};
const label = key => LABELS[key] || titleCase(key);

export function RosaCartesiana({ rose, title }) {
  if (!rose) return null;
  const position = (cell) => ({ cx: 160 + cell.x * 112, cy: 158 - cell.y * 112 });
  return <section className="kd9-card rounded-2xl p-4 sm:p-6 space-y-2" data-testid="numerology-rosa">
    <h3 className="text-lg font-cinzel text-amber-300"><Compass size={18} className="inline mr-2"/>{title}</h3>
    <p className="text-xs text-white/65">Rosa Cartesiana KD9 · Fuego, Agua, Aire, Tierra y Centro 9. Cada matriz se muestra por separado.</p>
    <svg viewBox="0 0 320 325" role="img" aria-label={title + ": Rosa Cartesiana numerológica"} className="block mx-auto w-full max-w-sm">
      <defs><radialGradient id="roseGold" cx="50%" cy="50%" r="60%"><stop offset="0%" stopColor="#a67f2b" stopOpacity=".28"/><stop offset="100%" stopColor="#070706" stopOpacity="0"/></radialGradient></defs>
      <circle cx="160" cy="158" r="145" fill="url(#roseGold)"/>
      <circle cx="160" cy="158" r="130" fill="none" stroke="#ad8739" strokeOpacity=".4"/>
      <circle cx="160" cy="158" r="84" fill="none" stroke="#ad8739" strokeOpacity=".23" strokeDasharray="4 5"/>
      <path d="M160 12V304 M14 158H306 M57 55L263 261 M263 55L57 261" stroke="#a5894d" strokeOpacity=".32"/>
      <g fill="#e2bf61" fontSize="11" textAnchor="middle">
        <text x="160" y="15">NORTE · AGUA</text><text x="160" y="323">SUR · FUEGO</text>
        <text x="35" y="145">TIERRA</text><text x="285" y="145">AIRE</text>
      </g>
      {(rose.cells||[]).map(cell=>{
        const {cx,cy}=position(cell), present=cell.count>0;
        return <g key={cell.number}>
          <circle cx={cx} cy={cy} r="24" fill={present?"#4b3819":"#100f0b"} stroke={present?"#f1cb65":"#715e35"} strokeWidth={present?2:1} opacity={present?1:.68}/>
          <text x={cx} y={cy+6} textAnchor="middle" fontFamily="serif" fontSize="21" fill={present?"#ffe19a":"#967d47"}>{cell.number}</text>
          {cell.count > 1 && <text x={cx+20} y={cy-18} fill="#f5d486" fontSize="11" textAnchor="middle">×{cell.count}</text>}
        </g>;
      })}
    </svg>
    <p className="text-[15px] leading-7 text-white/80">La Rosa Cartesiana organiza visualmente los códigos de esta matriz según sus posiciones y elementos KD9. {rose.repetitions?.length
      ? "Los valores "+rose.repetitions.join(", ")+" aparecen más de una vez, por lo que podrían señalar temas simbólicos recurrentes en estas posiciones."
      : "No aparecen códigos repetidos en las posiciones representadas."} Cada repetición debe interpretarse según su función, no como una suma adicional.</p>
    <p className="text-[15px] leading-7 text-white/80">{rose.absences?.length
      ? "Los valores "+rose.absences.join(", ")+" no están presentes en este conjunto específico de cálculos. Su ausencia es matemática, no significa que te falte una capacidad, una emoción o una cualidad humana."
      : "Los nueve valores aparecen representados en esta matriz. Esto no implica que la persona posea todas las cualidades descritas ni asegura ningún resultado."}</p>
  </section>;
}

export function SymbolCard({ title, value }) {
  if (!value) return null;
  return <article className="rounded-2xl border border-amber-400/25 bg-[#0d0b08] p-5 sm:p-7 space-y-4 min-w-0">
    <div className="flex flex-wrap items-baseline gap-3">
      <h4 className="font-cinzel text-lg text-amber-200">{title}</h4>
      <span className="text-3xl font-cinzel text-amber-300" aria-label={"Código "+displayNumber(value)}>{displayNumber(value)}</span>
    </div>
    <p className="text-sm italic text-amber-200/80">{value.archetype} · {value.direction} · {value.element}</p>
    {(value.narrative?.length ? value.narrative : [
      "Esta posición permite reflexionar sobre "+(value.context || "una parte de tu mapa")+". Su significado numérico es una correspondencia simbólica que debe compararse con tu experiencia.",
      "No permite asegurar qué ha ocurrido ni predecir qué sucederá. "+(value.question || "La interpretación debe mantenerse abierta a tu propia experiencia.")
    ]).map((paragraph, i)=><p key={i} className="text-[15px] sm:text-base leading-7 sm:leading-8 text-white/85">{paragraph}</p>)}
  </article>;
}
function Matrix({ title, items }) {
  return <section className="space-y-3">
    <h3 className="font-cinzel text-xl text-amber-300">{title}</h3>
    <div className="space-y-4">
      {Object.entries(items||{}).filter(([,v])=>v && typeof v === "object" && v.base !== undefined)
        .map(([key,value])=><SymbolCard key={key} title={label(key)} value={value}/>)}
    </div>
  </section>;
}

export function CompleteNumerology({ map, today }) {
  if (!map?.profile_complete) return null;
  const merged=map.cycles || {}; // Full symbolic codes; /today remains a lightweight update.
  return <div className="space-y-5" data-testid="numerology-complete-report">
    <div className="kd9-card p-5 space-y-2">
      <h2 className="font-cinzel text-xl text-amber-300">Tu estudio numerológico automático</h2>
      <p className="text-[15px] leading-7 text-white/80">Este estudio conecta tu nacimiento, nombre, apellidos proporcionados y ciclos temporales para ofrecer una lectura numerológica KD9 detallada. Cada sección explica qué representa el código, qué situaciones podrías reconocer, qué desafíos invita a observar y qué acciones conscientes podrías ensayar. Las historias familiares y las emociones personales que no hayas declarado no se darán por ciertas.</p>
      <p className="text-xs text-white/60">Protocolo {map.version} · {map.source}</p>
    </div>
    <Matrix title="Matriz natal · origen y propósito" items={map.birth}/>
    <RosaCartesiana title="Rosa Cartesiana natal" rose={map.roses?.natal}/>
    <Matrix title="Matriz del nombre · alma y expresión" items={map.name}/>
    <div className="kd9-card p-5 space-y-3">
      <h3 className="font-cinzel text-lg text-amber-300">Componentes del nombre</h3>
      <div className="space-y-4">
        {(map.name?.components||[]).map((v,i)=><SymbolCard key={i} title={v.name} value={v.value}/>)}
      </div>
    </div>
    <RosaCartesiana title="Rosa del nombre" rose={map.roses?.nombre}/>
    <Matrix title="Linajes · nombres familiares" items={map.lineages}/>
    {Object.values(map.lineages||{}).every(x=>!x) &&
      <p className="text-sm text-amber-200/80">Para separar el cálculo de cada linaje, añade opcionalmente los apellidos en el formulario. No se deducen parentescos por el orden del nombre.</p>}
    <Matrix title="Códigos complementarios" items={map.complementary}/>
    <div className="kd9-card p-5 text-sm text-white/75 space-y-2">
      <h3 className="font-cinzel text-lg text-amber-300">Maestros, aprendizajes y patrones</h3>
      <p className="text-[15px] leading-7">En los cálculos centrales {map.complementary?.maestros_detectados?.length
        ? "aparecen los códigos maestros "+map.complementary.maestros_detectados.join(", ")+". KD9 conserva estos pasos intermedios y los interpreta en su posición, sin convertirlos en una jerarquía de personas."
        : "no se identificaron códigos maestros. Esto no supone menos capacidad ni una lectura inferior."}</p>
      <p className="text-[15px] leading-7">{map.complementary?.codigos_de_aprendizaje_detectados?.length
        ? "El recorrido también conserva los compuestos de aprendizaje "+map.complementary.codigos_de_aprendizaje_detectados.join(", ")+". Puedes usarlos para plantear preguntas, pero no representan deudas o castigos demostrados."
        : "No aparecen compuestos de aprendizaje destacados en los ejes centrales. El mapa sigue teniendo recursos y desafíos que requieren contexto."}</p>
      <p className="text-[15px] leading-7">{map.complementary?.lecciones_por_ausencia_nominal?.length
        ? "Al revisar las letras del nombre no aparecen valores asociados con "+map.complementary.lecciones_por_ausencia_nominal.join(", ")+". En KD9 son ausencias de una tabla, no carencias de personalidad, y no permiten deducir ninguna experiencia."
        : "Los nueve valores aparecen en las letras analizadas. Su presencia matemática no implica poseer todas las virtudes ni estar libre de desafíos."}</p>
      <p className="text-[15px] leading-7">{map.integration?.convergences?.length
        ? "Algunas bases vuelven a aparecer en más de una función del mapa: "+map.integration.convergences.map(v=>"el "+v.number+" entre "+v.positions.join(" y ")).join("; ")+". Esta repetición invita a explorar qué temas tienen sentido para ti, sin sumar matrices diferentes ni afirmar causas ocultas."
        : "Las bases principales no muestran repeticiones destacadas; cada posición conserva su sentido específico."}</p>
    </div>
    <Matrix title="Mapa temporal · ciclos" items={merged}/>
    <RosaCartesiana title="Rosa de los ciclos" rose={map.roses?.temporal}/>
    <div className="kd9-card p-5 space-y-3">
      <h3 className="font-cinzel text-xl text-amber-300">Matriz Emocional Numerológica · preliminar</h3>
      <p className="text-[15px] leading-7 text-white/85">Esta parte del estudio relaciona las posiciones numerológicas con preguntas sobre cómo interpretas y afrontas tus experiencias. La presencia del arquetipo {map.integration?.matrix_emocional?.symbolic_theme} propone un tema de reflexión, pero no demuestra que estés atravesando una crisis, que hayas sufrido una herida o que tengas una emoción determinada.</p>
      <p className="text-[15px] leading-7 text-white/85">Para desarrollar una Matriz Emocional verdaderamente personalizada se necesitan tus propias respuestas sobre emociones, situaciones, conductas y necesidades. Puedes comenzar por preguntarte: {map.integration?.matrix_emocional?.reflection_question} El sistema mantendrá como desconocido aquello que no hayas confirmado.</p>
    </div>
    <div className="kd9-card p-5 space-y-3">
      <h3 className="font-cinzel text-xl text-amber-300">Integración y acciones KD9</h3>
      {(map.integration?.narrative || []).map((paragraph,i)=><p key={i} className="text-[15px] sm:text-base leading-7 sm:leading-8 text-white/85">{paragraph}</p>)}
      <p className="text-xs text-white/60">La auditoría matemática {map.audit?.verified?"comprobó que vocales y consonantes suman el mismo total del nombre":"continúa pendiente"}. Los compuestos permanecen visibles y las matrices conservan funciones diferenciadas.</p>
    </div>
  </div>;
}

export function FullSinastry({ report }) {
  if (!report) return null;
  const reading = report.relationship_reading;
  const chapters = reading?.chapters || [];
  return <section data-testid="synastry-person-a-b" className="space-y-5" aria-label="Resultado integrado de sinastría KD9">
    <div className="kd9-card rounded-2xl p-5 sm:p-7 space-y-4">
      <h2 className="font-cinzel text-xl sm:text-2xl text-amber-300">
        <HeartHandshake size={23} className="inline mr-2"/>Resultado de la sinastría KD9
      </h2>
      <p className="text-sm sm:text-base leading-7 text-white/85">
        {report.names?.a || "Persona A"} y {report.names?.b || "Persona B"}
      </p>
      {reading && <>
        <p className="font-cinzel text-lg text-amber-200" data-testid="sinastry-affinity">{reading.heading}</p>
        <p className="text-[15px] sm:text-base leading-7 text-white/85">
          El número simbólico central de su vínculo es <strong className="font-cinzel text-amber-300">{reading.bond_code}</strong>.
          A continuación encontrarás una interpretación de la relación construida al
          combinar sus números, sin repetir los dos estudios individuales.
        </p>
      </>}
      <p className="text-xs leading-5 text-white/60">{reading?.notice || report.context}</p>
    </div>
    {chapters.length ? chapters.map(chapter => <article
      key={chapter.key}
      data-testid={"sinastry-chapter-" + chapter.key}
      className="kd9-card rounded-2xl p-5 sm:p-7 space-y-4"
    >
      <h3 className="font-cinzel text-xl text-amber-300">{chapter.title}</h3>
      {(chapter.paragraphs || []).map((paragraph, index) =>
        <p key={index} className="text-[15px] sm:text-base leading-7 sm:leading-8 text-white/85">
          {paragraph}
        </p>
      )}
    </article>) : <div className="kd9-card rounded-2xl p-5 sm:p-7 space-y-3">
      <h3 className="font-cinzel text-xl text-amber-300">Interpretación conjunta</h3>
      {(report.integration?.narrative || []).map((paragraph, index) =>
        <p key={index} className="text-[15px] sm:text-base leading-7 sm:leading-8 text-white/85">{paragraph}</p>)}
    </div>}
    <p className="text-xs leading-5 text-white/60 px-2">
      {report.data_retention} Las interpretaciones son simbólicas y deben contrastarse
      con las experiencias, decisiones y acuerdos reales de ambas personas.
    </p>
  </section>;
}

export function NumberPairResult({ result }) {
  if (!result) return null;
  return <div className="kd9-card p-4 space-y-2" data-testid="numerology-number-pair">
    <h4 className="font-cinzel text-amber-300">Número A {displayNumber(result.a)} ↔ Número B {displayNumber(result.b)}</h4>
    {(result.narrative||[]).map((paragraph,j)=><p key={j} className="text-[15px] leading-7 text-white/85">{paragraph}</p>)}
  </div>;
}
